import React, { useEffect, useMemo, useState } from 'react';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  QuestionMarkCircleIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  FolderOpenIcon,
  ArrowRightIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  EllipsisVerticalIcon,
  TrashIcon,
  ArchiveBoxIcon,
  ClockIcon,
  ClipboardDocumentCheckIcon,
  DocumentMagnifyingGlassIcon,
} from '@heroicons/react/24/outline';

import type { Project, ProjectStatus } from '../types';
import { computeStats, formatRelative } from '../utils';
import { REFERENCE_NOW_ISO } from '../mocks';
import StatusBadge from '../components/StatusBadge';
import ProgressBar from '../components/ProgressBar';

interface Props {
  projects: Project[];
  onOpenProject: (id: string) => void;
  onCreateProject: (name: string, reference?: string) => void;
  onDeleteProject: (id: string) => void;
}

type FilterKey = 'all' | ProjectStatus;

type SortKey = 'name' | 'status' | 'candidates' | 'criteria' | 'progress' | 'updatedAt';
type SortDir = 'asc' | 'desc';

const FILTER_ORDER: FilterKey[] = ['all', 'in_progress', 'completed', 'archived'];
const FILTER_LABELS: Record<FilterKey, string> = {
  all: 'Tous',
  in_progress: 'En cours',
  completed: 'Complétés',
  archived: 'Archivés',
};

const STATUS_WEIGHT: Record<ProjectStatus, number> = {
  in_progress: 0,
  completed: 1,
  archived: 2,
};

const RESUME_KEY = 'analyse-ao/last-project-id';

const ProjectsDashboard: React.FC<Props> = ({
  projects,
  onOpenProject,
  onCreateProject,
  onDeleteProject,
}) => {
  const [filter, setFilter] = useState<FilterKey>('all');
  const [query, setQuery] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<Project | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('updatedAt');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [resumeDismissed, setResumeDismissed] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  // ── Dernier projet travaillé (reprise) ───────────────────────────
  const lastProjectId = useMemo(() => {
    if (typeof window === 'undefined') return null;
    try {
      return window.localStorage.getItem(RESUME_KEY);
    } catch {
      return null;
    }
  }, []);

  const resumeProject = useMemo(() => {
    if (!lastProjectId || resumeDismissed) return null;
    return projects.find(p => p.id === lastProjectId && p.status !== 'archived') ?? null;
  }, [projects, lastProjectId, resumeDismissed]);

  // ── Alertes actionnables ─────────────────────────────────────────
  const alerts = useMemo(() => {
    const out: { key: string; label: string; icon: React.ReactNode; tone: 'amber' | 'violet' }[] = [];
    let toValidate = 0;
    let missingRc = 0;
    let toValidateCriteria = 0;
    for (const p of projects) {
      if (p.status !== 'in_progress') continue;
      toValidate += p.evaluations.filter(
        e => e.aiScore !== undefined && e.userScore === undefined
      ).length;
      if (!computeStats(p).hasRegulation) missingRc += 1;
      if (!p.criteriaValidated && p.criteria.length > 0) toValidateCriteria += 1;
    }
    if (toValidate > 0) {
      out.push({
        key: 'eval',
        label: `${toValidate} évaluation${toValidate > 1 ? 's' : ''} à valider`,
        icon: <ClipboardDocumentCheckIcon className="w-4 h-4" />,
        tone: 'amber',
      });
    }
    if (toValidateCriteria > 0) {
      out.push({
        key: 'crit',
        label: `${toValidateCriteria} extraction${toValidateCriteria > 1 ? 's' : ''} de critères à relire`,
        icon: <DocumentMagnifyingGlassIcon className="w-4 h-4" />,
        tone: 'violet',
      });
    }
    if (missingRc > 0) {
      out.push({
        key: 'rc',
        label: `${missingRc} projet${missingRc > 1 ? 's' : ''} sans règlement de consultation`,
        icon: <ExclamationTriangleIcon className="w-4 h-4" />,
        tone: 'amber',
      });
    }
    return out;
  }, [projects]);

  // ── Enrichissement des lignes (pour tri + affichage) ────────────
  const rows = useMemo(() => {
    return projects.map(p => {
      const stats = computeStats(p);
      return { p, stats };
    });
  }, [projects]);

  const filtered = useMemo(() => {
    let list = rows;
    if (filter !== 'all') list = list.filter(r => r.p.status === filter);
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter(
        r =>
          r.p.name.toLowerCase().includes(q) ||
          (r.p.reference?.toLowerCase().includes(q) ?? false)
      );
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'name':
          cmp = a.p.name.localeCompare(b.p.name, 'fr');
          break;
        case 'status':
          cmp = STATUS_WEIGHT[a.p.status] - STATUS_WEIGHT[b.p.status];
          break;
        case 'candidates':
          cmp = a.stats.candidateCount - b.stats.candidateCount;
          break;
        case 'criteria':
          cmp = a.stats.activeLeafCriteriaCount - b.stats.activeLeafCriteriaCount;
          break;
        case 'progress':
          cmp = a.stats.progressPct - b.stats.progressPct;
          break;
        case 'updatedAt':
        default:
          cmp = a.p.updatedAt.localeCompare(b.p.updatedAt);
          break;
      }
      return cmp * dir;
    });
  }, [rows, filter, query, sortKey, sortDir]);

  const filterCounts: Record<FilterKey, number> = useMemo(
    () => ({
      all: projects.length,
      in_progress: projects.filter(p => p.status === 'in_progress').length,
      completed: projects.filter(p => p.status === 'completed').length,
      archived: projects.filter(p => p.status === 'archived').length,
    }),
    [projects]
  );

  // ── Click-outside pour fermer le menu ⋮ ──────────────────────────
  useEffect(() => {
    if (!openMenuId) return;
    const close = () => setOpenMenuId(null);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [openMenuId]);

  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'name' ? 'asc' : 'desc');
    }
  };

  const handleOpen = (id: string) => {
    try {
      window.localStorage.setItem(RESUME_KEY, id);
    } catch {
      /* storage unavailable, ignore */
    }
    onOpenProject(id);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header de page */}
        <header className="mb-6 flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-violet-700 leading-tight">
              Analyse d'appels d'offres
            </h1>
            <p className="mt-2 text-base text-gray-600">
              Évaluez les mémoires techniques de vos candidats, critère par critère.
            </p>
          </div>
          <div className="relative group">
            <button
              className="mt-1 text-gray-400 hover:text-violet-600 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2 rounded-lg"
              aria-label="Aide sur le workflow d'analyse"
            >
              <QuestionMarkCircleIcon className="w-6 h-6" />
            </button>
            <div className="absolute right-0 top-full mt-2 w-72 bg-gray-900 text-white text-xs rounded-lg p-3 shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-40">
              <p className="font-semibold mb-1.5">Comment ça marche</p>
              <ul className="space-y-1 leading-relaxed">
                <li>Déposez le règlement de consultation et les mémoires techniques des candidats.</li>
                <li>L'IA extrait les critères du RC, vous les validez.</li>
                <li>Pour chaque candidat × critère, l'IA propose des citations et une analyse — vous tranchez la note finale.</li>
                <li>À la fin, un RAO est généré à partir de <em>vos</em> évaluations.</li>
              </ul>
            </div>
          </div>
        </header>

        {/* Bandeau reprise (conditionnel) */}
        {resumeProject && (
          <ResumeBanner
            project={resumeProject}
            onOpen={() => handleOpen(resumeProject.id)}
            onDismiss={() => setResumeDismissed(true)}
          />
        )}

        {/* Alertes actionnables (conditionnel) */}
        {alerts.length > 0 && (
          <div
            className="mb-5 flex items-start gap-3 p-3.5 bg-amber-50 border border-amber-200 rounded-lg"
            role="status"
            aria-label="Alertes et éléments en attente"
          >
            <ExclamationTriangleIcon className="shrink-0 w-5 h-5 text-amber-700 mt-0.5" aria-hidden />
            <div className="flex-1 min-w-0 flex flex-wrap items-center gap-x-4 gap-y-1">
              {alerts.map((a, i) => (
                <React.Fragment key={a.key}>
                  {i > 0 && <span className="text-amber-400" aria-hidden>·</span>}
                  <span className="inline-flex items-center gap-1.5 text-sm text-amber-900">
                    <span className={a.tone === 'violet' ? 'text-violet-700' : 'text-amber-700'} aria-hidden>
                      {a.icon}
                    </span>
                    {a.label}
                  </span>
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        {/* Barre de filtres + actions */}
        <section className="mb-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* Filtres pills */}
            <nav className="flex items-center gap-1 flex-wrap" aria-label="Filtre par statut">
              {FILTER_ORDER.map(key => {
                const active = filter === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setFilter(key)}
                    className={
                      active
                        ? 'px-3 py-1.5 text-sm font-medium rounded-lg bg-violet-50 text-violet-700 border border-violet-200 transition-colors'
                        : 'px-3 py-1.5 text-sm font-medium rounded-lg text-gray-600 hover:bg-gray-50 border border-transparent transition-colors'
                    }
                    aria-pressed={active}
                  >
                    {FILTER_LABELS[key]}
                    <span
                      className={`ml-1.5 text-xs tabular-nums ${
                        active ? 'text-violet-600' : 'text-gray-400'
                      }`}
                    >
                      {filterCounts[key]}
                    </span>
                  </button>
                );
              })}
            </nav>

            {/* Search + CTA */}
            <div className="flex items-center gap-2">
              <div className="relative flex-1 lg:flex-none">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                <input
                  type="search"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder="Rechercher un projet…"
                  className="w-full lg:w-72 pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:border-violet-500"
                />
              </div>
              <button
                type="button"
                onClick={() => setIsCreateOpen(true)}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2 whitespace-nowrap"
              >
                <PlusIcon className="w-4 h-4" />
                Nouveau projet
              </button>
            </div>
          </div>
        </section>

        {/* Tableau projets */}
        {filtered.length === 0 ? (
          <EmptyProjects
            hasProjects={projects.length > 0}
            onReset={() => {
              setFilter('all');
              setQuery('');
            }}
            onCreate={() => setIsCreateOpen(true)}
          />
        ) : (
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 z-10 bg-gray-50 border-b border-gray-200">
                  <tr>
                    <SortableTh label="Réf." onClick={() => handleSort('name')} align="left" className="w-28 pl-5">
                      {null /* reference tri avec name */}
                    </SortableTh>
                    <SortableTh
                      label="Projet"
                      onClick={() => handleSort('name')}
                      active={sortKey === 'name'}
                      dir={sortDir}
                    />
                    <SortableTh
                      label="Statut"
                      onClick={() => handleSort('status')}
                      active={sortKey === 'status'}
                      dir={sortDir}
                      className="w-28"
                    />
                    <SortableTh
                      label="Candidats"
                      onClick={() => handleSort('candidates')}
                      active={sortKey === 'candidates'}
                      dir={sortDir}
                      align="right"
                      className="w-24"
                    />
                    <SortableTh
                      label="Critères"
                      onClick={() => handleSort('criteria')}
                      active={sortKey === 'criteria'}
                      dir={sortDir}
                      align="right"
                      className="w-24"
                    />
                    <SortableTh
                      label="Avancement"
                      onClick={() => handleSort('progress')}
                      active={sortKey === 'progress'}
                      dir={sortDir}
                      className="w-48"
                    />
                    <th className="w-10 pr-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(({ p, stats }) => {
                    const toValidateHere = p.evaluations.filter(
                      e => e.aiScore !== undefined && e.userScore === undefined
                    ).length;
                    const needsCriteriaValidation = !p.criteriaValidated && p.criteria.length > 0;
                    const hasRegulation = stats.hasRegulation;
                    const rowIsActionable = toValidateHere > 0 || needsCriteriaValidation || !hasRegulation;
                    return (
                      <tr
                        key={p.id}
                        tabIndex={0}
                        onClick={() => handleOpen(p.id)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleOpen(p.id);
                          }
                        }}
                        className="group border-b border-gray-100 last:border-b-0 hover:bg-violet-50/40 focus-visible:bg-violet-50/60 focus-visible:outline-none cursor-pointer transition-colors"
                      >
                        {/* Référence */}
                        <td className="py-3 pl-5 pr-2 align-middle">
                          <span className="text-xs font-medium text-gray-600 tabular-nums">
                            {p.reference ?? <span className="text-gray-400">—</span>}
                          </span>
                        </td>
                        {/* Nom + date + mini alertes */}
                        <td className="py-3 pr-3 align-middle min-w-0">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="font-semibold text-gray-900 truncate">
                              {p.name}
                            </span>
                            {rowIsActionable && (
                              <span
                                className="shrink-0 inline-block w-1.5 h-1.5 rounded-full bg-amber-500"
                                aria-label="Actions en attente"
                                title="Actions en attente"
                              />
                            )}
                          </div>
                          <div className="mt-0.5 flex items-center gap-1 text-xs text-gray-500">
                            <ClockIcon className="w-3 h-3" aria-hidden />
                            <span>Modifié {formatRelative(p.updatedAt, REFERENCE_NOW_ISO)}</span>
                          </div>
                        </td>
                        {/* Statut */}
                        <td className="py-3 pr-3 align-middle">
                          <StatusBadge status={p.status} />
                        </td>
                        {/* Candidats */}
                        <td className="py-3 pr-3 align-middle text-right">
                          <span className="text-sm text-gray-900 tabular-nums font-medium">
                            {stats.candidateCount}
                          </span>
                        </td>
                        {/* Critères */}
                        <td className="py-3 pr-3 align-middle text-right">
                          {stats.criteriaCount === 0 ? (
                            <span className="text-xs text-gray-400">—</span>
                          ) : (
                            <span className="text-sm text-gray-900 tabular-nums font-medium">
                              {stats.activeLeafCriteriaCount}
                              <span className="text-gray-400 font-normal">
                                /{stats.criteriaCount}
                              </span>
                            </span>
                          )}
                        </td>
                        {/* Avancement */}
                        <td className="py-3 pr-4 align-middle">
                          {stats.evaluationsExpected === 0 ? (
                            <span className="text-xs text-gray-400">Pas encore démarré</span>
                          ) : (
                            <div className="flex items-center gap-2">
                              <div className="flex-1">
                                <ProgressBar
                                  value={stats.progressPct}
                                  tone={p.status === 'completed' ? 'emerald' : 'violet'}
                                />
                              </div>
                              <span className="shrink-0 text-xs text-gray-600 tabular-nums w-10 text-right">
                                {stats.progressPct}%
                              </span>
                            </div>
                          )}
                        </td>
                        {/* Actions ⋮ */}
                        <td className="py-3 pr-3 align-middle text-right relative">
                          <button
                            type="button"
                            onClick={e => {
                              e.stopPropagation();
                              setOpenMenuId(cur => (cur === p.id ? null : p.id));
                            }}
                            className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100 p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
                            aria-label={`Actions pour ${p.name}`}
                            aria-haspopup="menu"
                            aria-expanded={openMenuId === p.id}
                          >
                            <EllipsisVerticalIcon className="w-4 h-4" />
                          </button>
                          {openMenuId === p.id && (
                            <div
                              role="menu"
                              onClick={e => e.stopPropagation()}
                              className="absolute right-3 top-full mt-1 z-20 w-48 bg-white border border-gray-200 rounded-lg shadow-lg py-1"
                            >
                              <button
                                type="button"
                                role="menuitem"
                                onClick={() => {
                                  setOpenMenuId(null);
                                  handleOpen(p.id);
                                }}
                                className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                              >
                                <ArrowRightIcon className="w-4 h-4 text-gray-400" />
                                Ouvrir
                              </button>
                              <button
                                type="button"
                                role="menuitem"
                                disabled
                                className="w-full px-3 py-2 text-left text-sm text-gray-400 flex items-center gap-2 cursor-not-allowed"
                                title="Bientôt disponible"
                              >
                                <ArchiveBoxIcon className="w-4 h-4" />
                                Archiver
                              </button>
                              <div className="my-1 border-t border-gray-100" />
                              <button
                                type="button"
                                role="menuitem"
                                onClick={() => {
                                  setOpenMenuId(null);
                                  setPendingDelete(p);
                                }}
                                className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                              >
                                <TrashIcon className="w-4 h-4" />
                                Supprimer
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Modale Nouveau projet */}
      {isCreateOpen && (
        <CreateProjectModal
          onClose={() => setIsCreateOpen(false)}
          onCreate={(name, ref) => {
            onCreateProject(name, ref);
            setIsCreateOpen(false);
          }}
        />
      )}

      {/* Modale Confirmation suppression */}
      {pendingDelete && (
        <ConfirmDeleteModal
          project={pendingDelete}
          onCancel={() => setPendingDelete(null)}
          onConfirm={() => {
            onDeleteProject(pendingDelete.id);
            setPendingDelete(null);
          }}
        />
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Sous-composants locaux
// ─────────────────────────────────────────────────────────────────────

interface SortableThProps {
  label: string;
  onClick?: () => void;
  active?: boolean;
  dir?: SortDir;
  align?: 'left' | 'right';
  className?: string;
  children?: React.ReactNode;
}

const SortableTh: React.FC<SortableThProps> = ({
  label,
  onClick,
  active,
  dir,
  align = 'left',
  className = '',
  children,
}) => {
  const alignClass = align === 'right' ? 'text-right' : 'text-left';
  const justify = align === 'right' ? 'justify-end' : 'justify-start';
  return (
    <th
      scope="col"
      className={`py-2.5 ${alignClass} ${className} font-medium text-xs uppercase tracking-wide text-gray-600`}
    >
      {onClick ? (
        <button
          type="button"
          onClick={onClick}
          className={`inline-flex items-center gap-1 ${justify} w-full hover:text-gray-900 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 rounded`}
        >
          <span>{label}</span>
          {active ? (
            dir === 'asc' ? (
              <ArrowUpIcon className="w-3 h-3" />
            ) : (
              <ArrowDownIcon className="w-3 h-3" />
            )
          ) : (
            <span className="w-3 h-3" aria-hidden />
          )}
        </button>
      ) : (
        <span>{label}</span>
      )}
      {children}
    </th>
  );
};

interface ResumeBannerProps {
  project: Project;
  onOpen: () => void;
  onDismiss: () => void;
}

const ResumeBanner: React.FC<ResumeBannerProps> = ({ project, onOpen, onDismiss }) => {
  const stats = computeStats(project);
  return (
    <div
      className="mb-5 flex items-center gap-3 p-4 bg-white border border-violet-200 rounded-lg shadow-sm"
      role="region"
      aria-label="Reprise du dernier projet"
    >
      <span className="shrink-0 p-2 rounded-lg bg-violet-50 text-violet-700" aria-hidden>
        <ClockIcon className="w-5 h-5" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium text-violet-700 uppercase tracking-wide">
          Reprendre là où vous en étiez
        </div>
        <div className="mt-0.5 flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-gray-900 truncate">{project.name}</span>
          {project.reference && (
            <span className="text-xs text-gray-500 tabular-nums">· {project.reference}</span>
          )}
          <span className="text-xs text-gray-500">
            · dernière activité {formatRelative(project.updatedAt, REFERENCE_NOW_ISO)}
          </span>
          {stats.evaluationsExpected > 0 && (
            <span className="text-xs text-gray-500 tabular-nums">
              · {stats.evaluationsDone}/{stats.evaluationsExpected} évaluations
            </span>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={onOpen}
        className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2"
      >
        Ouvrir
        <ArrowRightIcon className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={onDismiss}
        className="shrink-0 p-1 rounded-lg text-gray-400 hover:bg-gray-50 hover:text-gray-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
        aria-label="Masquer le bandeau de reprise"
      >
        <XMarkIcon className="w-5 h-5" />
      </button>
    </div>
  );
};

interface EmptyProjectsProps {
  hasProjects: boolean;
  onReset: () => void;
  onCreate: () => void;
}

const EmptyProjects: React.FC<EmptyProjectsProps> = ({ hasProjects, onReset, onCreate }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
    <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-violet-50 text-violet-700 mb-4">
      <FolderOpenIcon className="w-6 h-6" />
    </div>
    <h3 className="text-lg font-semibold text-gray-900 mb-1">
      {hasProjects ? 'Aucun projet ne correspond' : 'Aucun projet pour le moment'}
    </h3>
    <p className="text-sm text-gray-600 mb-5 max-w-md mx-auto">
      {hasProjects
        ? 'Essayez de modifier les filtres ou la recherche pour retrouver un projet.'
        : 'Créez votre premier projet pour commencer à analyser un appel d\'offres.'}
    </p>
    {hasProjects ? (
      <button
        type="button"
        onClick={onReset}
        className="px-4 py-2 text-sm font-medium border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2"
      >
        Réinitialiser les filtres
      </button>
    ) : (
      <button
        type="button"
        onClick={onCreate}
        className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2"
      >
        <PlusIcon className="w-4 h-4" />
        Créer le premier projet
      </button>
    )}
  </div>
);

// ─── Modale : Nouveau projet ───────────────────────────────────────

interface CreateProjectModalProps {
  onClose: () => void;
  onCreate: (name: string, reference?: string) => void;
}

const CreateProjectModal: React.FC<CreateProjectModalProps> = ({ onClose, onCreate }) => {
  const [name, setName] = useState('');
  const [reference, setReference] = useState('');

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    onCreate(name.trim(), reference.trim() || undefined);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-title"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl max-w-md w-full p-6"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <h2 id="create-title" className="text-lg font-semibold text-gray-900">
            Nouveau projet d'analyse
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-gray-400 hover:bg-gray-50 hover:text-gray-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2"
            aria-label="Fermer"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label htmlFor="project-name" className="block text-xs font-medium text-gray-700 mb-1">
              Nom du projet
            </label>
            <input
              id="project-name"
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Ex : Rénovation énergétique Mairie…"
              autoFocus
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:border-violet-500"
            />
          </div>

          <div>
            <label htmlFor="project-ref" className="block text-xs font-medium text-gray-700 mb-1">
              Référence / n° de marché
              <span className="ml-1.5 text-gray-400 font-normal">(optionnel)</span>
            </label>
            <input
              id="project-ref"
              type="text"
              value={reference}
              onChange={e => setReference(e.target.value)}
              placeholder="Ex : AO-2026-042"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:border-violet-500 tabular-nums"
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={!name.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2"
            >
              Créer le projet
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Modale : Confirmation suppression ─────────────────────────────

interface ConfirmDeleteModalProps {
  project: Project;
  onCancel: () => void;
  onConfirm: () => void;
}

const ConfirmDeleteModal: React.FC<ConfirmDeleteModalProps> = ({ project, onCancel, onConfirm }) => {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-title"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-xl shadow-xl max-w-md w-full p-6"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-start gap-4">
          <div className="shrink-0 p-2 rounded-lg bg-red-50 text-red-600">
            <ExclamationTriangleIcon className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 id="delete-title" className="text-lg font-semibold text-gray-900 mb-1">
              Supprimer ce projet ?
            </h2>
            <p className="text-sm text-gray-600">
              Vous êtes sur le point de supprimer&nbsp;
              <span className="font-medium text-gray-900">« {project.name} »</span>.
              Cette action supprime également les fichiers déposés, les critères extraits
              et toutes les évaluations associées. Elle est définitive.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 mt-6 pt-4 border-t border-gray-100">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
          >
            Supprimer définitivement
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProjectsDashboard;
